import { useState, useEffect, useRef, useCallback } from 'react'
import { apiGet } from '../../../../generic/Api_Functions.jsx'
import { adaptApiData } from '../dataUtils.js'
import { durationStringToMilliseconds } from '../../../../generic/DateTimeFunctions.jsx'

/**
 * Custom hook to handle chart data logic.
 * @param {Object} config - Widget configuration.
 * @param {string} config.variableName - Name of the variable to query.
 * @param {string} config.format - Time format ('12h' or '24h').
 * @param {number} config.intervalMinutes - Update interval in minutes.
 * @returns {Object} Data state: dataPoints, loading, error, refreshData.
 */
const useGraphicData = ({ variableName, format, intervalMinutes = 30, timeRangeMode, time, startTime, endTime }) => {
  const [dataPoints, setDataPoints] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const initialized = useRef(false)

  const fetchData = useCallback(async () => {
    if (!variableName) return

    if (!initialized.current) setLoading(true)
    setError(null)

    try {
      let computedEndDate = new Date()
      let computedStartDate = new Date(computedEndDate.getTime() - 24 * 60 * 60 * 1000)

      if (timeRangeMode === 'range') {
        const startVal = startTime ? new Date(startTime) : null
        const endVal = endTime ? new Date(endTime) : null
        if (endVal && !isNaN(endVal)) computedEndDate = endVal
        if (startVal && !isNaN(startVal)) computedStartDate = startVal
      } else if (typeof time === 'string' && time.trim() !== '') {
        try {
          const durationMs = durationStringToMilliseconds(time)
          if (typeof durationMs === 'number' && !isNaN(durationMs) && durationMs > 0) {
            computedStartDate = new Date(computedEndDate.getTime() - durationMs)
          }
        } catch (e) {
        }
      }

      if (computedStartDate.getTime() > computedEndDate.getTime()) {
        const tmp = computedStartDate
        computedStartDate = computedEndDate
        computedEndDate = tmp
      }

      const params = new URLSearchParams()
      params.append('startDate', computedStartDate.toISOString())
      params.append('endDate', computedEndDate.toISOString())
      if (intervalMinutes) params.append('intervalMinutes', intervalMinutes)

      const result = await apiGet(`mos/getDeviceHistoryValue/${encodeURIComponent(variableName)}?${params.toString()}`)

      if (result) {
        const adapted = adaptApiData(result, format)
        setDataPoints(adapted)
      } else {
        setDataPoints([])
      }
    } catch (err) {
      console.error('Error fetching graphic data:', err)
      setError(err)
    } finally {
      setLoading(false)
      initialized.current = true
    }
  }, [variableName, format, intervalMinutes, timeRangeMode, time, startTime, endTime])

  useEffect(() => {
    fetchData()
    // Use intervalMinutes for polling frequency (converted to ms), defaulting to 30 minutes
    const pollingInterval = (intervalMinutes || 30) * 60 * 1000
    const interval = setInterval(fetchData, pollingInterval)
    return () => clearInterval(interval)
  }, [fetchData, intervalMinutes])

  return { dataPoints, loading, error, refreshData: fetchData }
}

export default useGraphicData
